
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminKycService, AdminKycRequest, AdminKycDetail } from '../../services/admin.kyc.service';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import {
  ShieldCheck,
  FileSearch,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  ChevronRight,
  Search,
  X,
  User,
  Calendar,
  AlertTriangle,
  Info,
  Loader2,
  FileText
} from 'lucide-react';

const AdminKyc: React.FC = () => {
  const [submissions, setSubmissions] = useState<AdminKycRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKycId, setSelectedKycId] = useState<string | null>(null);
  const [kycDetail, setKycDetail] = useState<AdminKycDetail | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    const response = await adminKycService.getKycRequests({ status: statusFilter, page });
    if (response.success) {
      setSubmissions(response.data);
    }
    setIsLoading(false);
  }, [statusFilter, page]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    if (selectedKycId) {
      fetchKycDetail(selectedKycId);
    } else {
      setKycDetail(null);
      setDocumentUrl(null);
    }
  }, [selectedKycId]);

  const fetchKycDetail = async (id: string) => {
    setIsDetailLoading(true);
    try {
      const response = await adminKycService.getKycDetail(id);
      if (response.success) {
        setKycDetail({
          ...response.data,
          status: response.data.status || submissions.find(s => s.id === id)?.status
        });

        // Step 2: Resolve the file projection URL
        if (response.data.fileKey) {
          const fileResponse = await adminKycService.getFileView(response.data.fileKey);
          if (fileResponse && fileResponse.url) {
            setDocumentUrl(fileResponse.url);
          }
        }
      } else {
        setToast({ message: response.message || 'Inspection Failure', type: 'error' });
        setSelectedKycId(null);
      }
    } catch (error) {
      setToast({ message: 'Audit error encountered', type: 'error' });
      setSelectedKycId(null);
    } finally {
      setIsDetailLoading(false);
    }
    setTimeout(() => setToast(null), 3000);
  };

  const filteredSubmissions = useMemo(() => {
    return submissions.filter(s =>
      (s.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (s.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (s.id?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );
  }, [submissions, searchQuery]);

  const handleAction = async (status: 'APPROVED' | 'REJECTED') => {
    if (!selectedKycId) return;
    if (status === 'REJECTED' && !rejectionReason) {
      setShowRejectForm(true);
      return;
    }

    setIsProcessing(true);
    let success = false;
    let response;

    if (status === 'APPROVED') {
      response = await adminKycService.approveKyc(selectedKycId, "Documents verified successfully");
    } else {
      response = await adminKycService.rejectKyc(selectedKycId, rejectionReason || "Document image not clear");
    }

    success = response.success;

    if (success) {
      setSubmissions(prev => prev.map(s =>
        s.id === selectedKycId ? { ...s, status } : s
      ));
      if (kycDetail) {
        setKycDetail({ ...kycDetail, status });
      }
      setToast({ message: `Audit Complete: KYC ${status}`, type: 'success' });
      setShowRejectForm(false);
      setRejectionReason('');
      fetchSubmissions();
    } else {
      setToast({ message: response.message || 'Operation Failure', type: 'error' });
    }
    setIsProcessing(false);
    setTimeout(() => setToast(null), 3000);
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'REJECTED': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'PENDING': return 'bg-brand-primary/10 text-brand-primary border-brand-primary/20';
      default: return 'bg-brand-muted/10 text-brand-muted border-brand-border';
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-24">

      {/* 1. Header & Quick Search */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Compliance Vault</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Regulatory Identity Auditing Queue</p>
        </div>

        <div className="w-full md:w-96 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" size={18} />
          <input
            type="text"
            placeholder="Search Identity Protocol..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-brand-card border border-brand-border rounded-xl pl-12 pr-4 py-3 text-sm text-white focus:outline-none focus:border-brand-primary transition-all"
          />
        </div>
      </section>

      {/* 2. Tabs - Filtering */}
      <div className="flex bg-brand-dark/40 p-1.5 rounded-2xl border border-brand-border w-fit">
        {['PENDING', 'APPROVED', 'REJECTED'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-8 py-2.5 rounded-xl text-[10px] font-black tracking-widest transition-all ${statusFilter === status ? 'bg-brand-primary text-brand-dark shadow-xl' : 'text-brand-muted hover:text-white'}`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* 3. Toast */}
      {toast && (
        <div className={`fixed top-8 right-8 z-[200] px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 animate-in slide-in-from-right-10 duration-500 ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
          <CheckCircle2 size={20} />
          <span className="text-sm font-black uppercase tracking-widest">{toast.message}</span>
        </div>
      )}

      {/* 4. Submissions Table/Cards */}
      <div className="min-h-[400px]">
        {/* Desktop Table */}
        <div className="hidden md:block bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl relative">
          {isLoading && (
            <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[2px] z-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-brand-primary" size={32} />
            </div>
          )}
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-brand-dark/40 border-b border-brand-border">
                <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Compliance ID</th>
                <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Entity Name</th>
                <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Email</th>
                <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Type</th>
                <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Status</th>
                <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em] text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {filteredSubmissions.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-brand-muted text-xs font-bold uppercase tracking-widest opacity-50">
                    No matching compliance logs found
                  </td>
                </tr>
              ) : (
                filteredSubmissions.map((s) => (
                  <tr key={s.id} className="hover:bg-brand-primary/5 transition-colors group">
                    <td className="px-6 py-5 font-mono text-xs text-brand-muted group-hover:text-brand-primary transition-colors">{s.id.slice(0, 8)}...</td>
                    <td className="px-6 py-5">
                      <p className="text-sm font-bold text-white">{s.name}</p>
                    </td>
                    <td className="px-6 py-5">
                      <p className="text-sm text-brand-muted">{s.email}</p>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-[10px] font-black text-brand-muted uppercase tracking-widest border border-brand-border px-2 py-0.5 rounded">
                        {s.documentType}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${getStatusStyle(s.status)}`}>
                        {s.status === 'PENDING' && <Clock size={12} />}
                        {s.status === 'APPROVED' && <CheckCircle2 size={12} />}
                        {s.status === 'REJECTED' && <XCircle size={12} />}
                        {s.status}
                      </div>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <button
                        onClick={() => setSelectedKycId(s.id)}
                        className="p-2.5 rounded-xl bg-brand-dark border border-brand-border text-brand-muted hover:text-white hover:border-brand-primary/30 transition-all"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {filteredSubmissions.map((s) => (
            <div key={s.id} className="bg-brand-card border border-brand-border rounded-2xl p-5 space-y-4 shadow-lg active:scale-[0.98] transition-transform">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <span className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Protocol {s.id.slice(0, 8)}...</span>
                  <h4 className="font-bold text-white text-sm truncate max-w-[150px]">{s.name}</h4>
                  <p className="text-[10px] text-brand-muted">{s.email}</p>
                </div>
                <div className={`px-2 py-1 rounded-lg text-[9px] font-bold uppercase border ${getStatusStyle(s.status)}`}>
                  {s.status}
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-brand-muted py-2 border-t border-brand-border/30">
                <span className="font-bold uppercase tracking-widest">{s.documentType}</span>
                <button
                  onClick={() => setSelectedKycId(s.id)}
                  className="text-brand-primary font-black uppercase tracking-widest flex items-center gap-1"
                >
                  AUDIT DETAILS <ChevronRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5. Inspection Modal */}
      {selectedKycId && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-0 md:p-6 bg-brand-dark/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-brand-card border-l border-brand-border w-full max-w-5xl h-full md:h-auto md:max-h-[90vh] md:rounded-3xl overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] flex flex-col animate-in zoom-in-95 duration-300">

            {/* Modal Header */}
            <div className="p-6 border-b border-brand-border flex items-center justify-between bg-brand-dark/40 shrink-0">
              <div>
                <h3 className="text-white font-bold uppercase tracking-tight flex items-center gap-2">
                  <ShieldCheck size={20} className="text-brand-primary" />
                  Compliance Audit: {selectedKycId.slice(0, 12)}...
                </h3>
                {kycDetail && (
                  <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest mt-0.5">Submitted On {new Date(kycDetail.submittedAt).toLocaleDateString()}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setSelectedKycId(null);
                  setShowRejectForm(false);
                }}
                className="p-3 rounded-2xl bg-brand-dark hover:bg-brand-border text-brand-muted hover:text-white transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-10 relative">
              {isDetailLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-brand-dark/20 backdrop-blur-sm z-20">
                  <div className="text-center space-y-4">
                    <Loader2 className="animate-spin text-brand-primary mx-auto" size={48} />
                    <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest">Decrypting Proof Vault...</p>
                  </div>
                </div>
              ) : kycDetail && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                  {/* Left Column: Visual Proof */}
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] flex items-center gap-2">
                        <FileSearch size={14} />
                        Visual Proof Protocol
                      </h4>
                      <div className="aspect-[16/10] bg-brand-dark border border-brand-border rounded-2xl overflow-hidden relative group">
                        {!documentUrl ? (
                          <div className="w-full h-full flex flex-col items-center justify-center text-brand-muted p-6 text-center space-y-2">
                            <Info size={32} className="opacity-20" />
                            <p className="text-[10px] font-black uppercase tracking-widest">No Proof Available</p>
                            <p className="text-[9px] opacity-60">Entity document is either missing or vault resolution failed.</p>
                          </div>
                        ) : documentUrl.toLowerCase().split('?')[0].endsWith('.pdf') ? (
                          <iframe
                            src={documentUrl}
                            className="w-full h-full border-none"
                            title="KYC Proof"
                          />
                        ) : (
                          <img
                            src={documentUrl}
                            alt="Government Issued ID"
                            className="w-full h-full object-cover transition-all duration-700"
                          />
                        )}
                        {documentUrl && (
                          <div className="absolute bottom-4 left-4 right-4 p-3 bg-brand-dark/80 backdrop-blur-md border border-brand-border rounded-xl">
                            <p className="text-[10px] font-black text-white uppercase tracking-widest">Doc Type: {kycDetail.documentType}</p>
                            <p className="text-[9px] text-brand-muted mt-1 uppercase">S3 Projection Active (5m EXP)</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-5 bg-brand-primary/5 border border-brand-primary/10 rounded-2xl flex items-start gap-4">
                      <AlertTriangle size={20} className="text-brand-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[10px] text-white font-bold uppercase tracking-widest mb-1">Audit Advisory</p>
                        <p className="text-[9px] text-brand-muted leading-relaxed uppercase font-bold tracking-widest">
                          Compare the document image with the provided entity metadata. Check for digital manipulation, expiration, or data discrepancies.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Metadata & Actions */}
                  <div className="space-y-8">
                    <div className="space-y-6">
                      <h4 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Verification Metadata</h4>
                      <div className="grid grid-cols-1 gap-4">
                        <AuditField icon={<User size={14} />} label="Full Name" value={kycDetail.name} />
                        <AuditField icon={<FileSearch size={14} />} label="Primary Email" value={kycDetail.email} />
                        <AuditField icon={<Calendar size={14} />} label="Submission Origin" value={new Date(kycDetail.submittedAt).toLocaleString()} />
                        <AuditField icon={<FileText size={14} />} label="Document Type" value={kycDetail.documentType} />
                      </div>
                    </div>

                    <div className="pt-6 border-t border-brand-border/30">
                      {kycDetail.status === 'PENDING' ? (
                        <>
                          {showRejectForm ? (
                            <div className="space-y-4 animate-in slide-in-from-top-4 duration-300">
                              <Input
                                label="Rejection Protocol Reason"
                                placeholder="e.g. Blurry image, Expired document..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="h-12"
                              />
                              <div className="flex gap-3">
                                <Button
                                  onClick={() => handleAction('REJECTED')}
                                  variant="danger"
                                  className="flex-1 h-12"
                                  isLoading={isProcessing}
                                >
                                  CONFIRM REJECTION
                                </Button>
                                <Button
                                  onClick={() => setShowRejectForm(false)}
                                  variant="secondary"
                                  className="px-6 h-12"
                                >
                                  BACK
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-4">
                              <Button
                                onClick={() => handleAction('APPROVED')}
                                className="h-14"
                                isLoading={isProcessing}
                              >
                                <CheckCircle2 size={18} />
                                APPROVE KYC
                              </Button>
                              <Button
                                onClick={() => setShowRejectForm(true)}
                                variant="danger"
                                className="h-14"
                              >
                                <XCircle size={18} />
                                REJECT KYC
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={`p-6 rounded-2xl border ${getStatusStyle(kycDetail.status || '')} text-center space-y-2`}>
                          <p className="text-xs font-black uppercase tracking-[0.2em]">Final Audit State: {kycDetail.status}</p>
                          {kycDetail.remarks && (
                            <p className="text-[10px] uppercase font-bold tracking-widest opacity-80">Reason: {kycDetail.remarks}</p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-brand-dark/50 rounded-xl border border-brand-border">
                      <Info size={14} className="text-brand-primary" />
                      <span className="text-[9px] text-brand-muted uppercase font-black tracking-widest">Admin review session logged securely.</span>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AuditField: React.FC<{ icon: React.ReactNode, label: string, value: string, mono?: boolean }> = ({ icon, label, value, mono }) => (
  <div className="flex items-center gap-4 py-3 border-b border-brand-border/30 last:border-0">
    <div className="w-8 h-8 rounded-lg bg-brand-dark border border-brand-border flex items-center justify-center text-brand-muted">
      {icon}
    </div>
    <div>
      <p className="text-[9px] text-brand-muted uppercase font-black tracking-widest mb-0.5">{label}</p>
      <p className={`text-sm font-bold text-white ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  </div>
);

export default AdminKyc;
